<template>
  <div>
    <!-- Dialog de Histórico (lista de registros) -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="900px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-gender-female</v-icon>
            <span class="ds-dialog-title">Histórico do ciclo menstrual</span>
          </div>
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="white"
                  @click="openAddForm"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Novo registro</span>
            </v-tooltip>
            <v-btn icon color="white" class="close-btn" @click="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section v-if="records.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 160px">
                    Registrado em
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Ciclo menstrual
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Climatério
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 90px">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, idx) in records"
                  :key="item.id"
                  class="history-row"
                  :class="{ 'latest-row': idx === 0 }"
                >
                  <td class="text-no-wrap pa-4">
                    <div class="d-flex align-center">
                      <v-chip
                        v-if="idx === 0"
                        x-small
                        color="catPurple"
                        text-color="white"
                        class="mr-2"
                      >
                        Atual
                      </v-chip>
                      <span class="body-2">{{ formatDate(item.recorded_at || item.created_at) }}</span>
                    </div>
                  </td>
                  <td class="pa-4">
                    <span class="body-2">{{ menstrualLabel(item.menstrual_status) }}</span>
                  </td>
                  <td class="pa-4">
                    <span class="body-2">{{ boolLabel(item.is_climacteric) }}</span>
                  </td>
                  <td class="pa-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="catPurple"
                          class="action-btn mr-1"
                          @click="openEditForm(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="error"
                          class="action-btn"
                          @click="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <!-- Estado vazio -->
          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">mdi-gender-female</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">Nenhum registro de ciclo menstrual.</p>
            <v-btn color="catPurple" outlined small class="mt-4" @click="openAddForm">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeiro registro
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Adicionar/Editar -->
    <v-dialog v-model="formDialogVisible" max-width="600px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center" style="position: relative; z-index: 1">
            <v-icon color="white" class="mr-2">
              {{ editingId ? "mdi-pencil" : "mdi-plus-circle-outline" }}
            </v-icon>
            <span class="ds-dialog-title">
              {{ editingId ? "Editar registro" : "Novo registro" }}
            </span>
          </div>
          <v-btn icon color="white" class="close-btn" @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- Obrigatórios vazios ficam em vermelho ao tentar salvar (validate). -->
          <v-form ref="entryForm" @submit.prevent>
            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-gender-female</v-icon>
                  <span>Ciclo Menstrual</span>
                </div>
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.menstrual_status"
                    :items="menstrualOptions"
                    label="Ciclo menstrual *"
                    :rules="[required]"
                    outlined
                    dense
                    clearable
                    color="catPurple"
                    item-color="catPurple"
                    hide-details="auto"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.is_climacteric"
                    :items="boolOptions"
                    label="Climatério? *"
                    :rules="[required]"
                    outlined
                    dense
                    clearable
                    color="catPurple"
                    item-color="catPurple"
                    hide-details="auto"
                  ></v-select>
                </v-col>
              </v-row>
            </section>

            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-note-text</v-icon>
                  <span>Observação</span>
                  <span class="ds-form-section__optional">opcional</span>
                </div>
              </div>
              <v-textarea
                v-model="form.notes"
                label="Observação"
                outlined
                dense
                rows="2"
                auto-grow
                color="catPurple"
                hide-details
              ></v-textarea>
            </section>
          </v-form>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="closeForm">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="catPurple"
            dark
            :loading="formLoading"
            depressed
            class="px-6"
            @click="saveData"
          >
            <v-icon small left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import { formatDate } from "@/utils/datetime";
import { required } from "@/utils/formRules";
import {
  createGynecology,
  updateGynecology,
  deleteGynecology,
} from "@/services/gynecologyService";

export default {
  name: "EditGynecologyDialog",

  props: {
    value: { type: Boolean, default: false },
    records: { type: Array, default: () => [] },
    patientId: { type: [String, Number], required: true },
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      formDialogVisible: false,
      editingId: null,
      form: this.blankForm(),
      formLoading: false,
      menstrualOptions: [
        { text: "Regular", value: "regular" },
        { text: "Irregular", value: "irregular" },
        { text: "Anticoncepcional", value: "anticoncepcional" },
        { text: "DIU", value: "diu" },
        { text: "Menopausa", value: "menopausa" },
      ],
      boolOptions: [
        { text: "Sim", value: true },
        { text: "Não", value: false },
      ],
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val && this.openDirectAdd) {
        this.$nextTick(() => this.openAddForm());
      }
    },
    formDialogVisible(val) {
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    formatDate: (v) => formatDate(v, { fallback: "—" }),
    // Regras dos campos (`:rules`) — ver utils/formRules.
    required,

    blankForm() {
      return {
        menstrual_status: null,
        is_climacteric: null,
        notes: "",
      };
    },

    menstrualLabel(s) {
      return (
        {
          regular: "Regular",
          irregular: "Irregular",
          anticoncepcional: "Anticoncepcional",
          diu: "DIU",
          menopausa: "Menopausa",
        }[s] || "—"
      );
    },

    boolLabel(v) {
      if (v === true || v === 1) return "Sim";
      if (v === false || v === 0) return "Não";
      return "—";
    },

    openAddForm() {
      this.editingId = null;
      this.form = this.blankForm();
      this.formDialogVisible = true;
      // Abre limpo: sem os vermelhos de uma tentativa anterior.
      this.$nextTick(() => this.$refs.entryForm && this.$refs.entryForm.resetValidation());
    },

    openEditForm(item) {
      this.editingId = item.id;
      this.form = {
        menstrual_status: item.menstrual_status ?? null,
        is_climacteric:
          item.is_climacteric === null || item.is_climacteric === undefined
            ? null
            : !!item.is_climacteric,
        notes: item.notes ?? "",
      };
      this.formDialogVisible = true;
      // Abre limpo: sem os vermelhos de uma tentativa anterior.
      this.$nextTick(() => this.$refs.entryForm && this.$refs.entryForm.resetValidation());
    },

    closeForm() {
      this.formDialogVisible = false;
      this.editingId = null;
      this.form = this.blankForm();
    },

    payload() {
      return {
        menstrual_status: this.form.menstrual_status,
        is_climacteric: this.form.is_climacteric,
        notes: this.form.notes?.trim() || null,
      };
    },

    async saveData() {
      // Campos obrigatórios vazios ficam em vermelho; não grava.
      if (!this.$refs.entryForm.validate()) return;
      this.formLoading = true;
      try {
        if (this.editingId) {
          await updateGynecology(this.editingId, this.payload());
        } else {
          await createGynecology(this.patientId, this.payload());
        }
        // salvia-781: guarda antes de fechar o form, que limpa a selecao.
        const registrado = this.payload();
        this.success("Registro salvo com sucesso!");
        this.closeForm();
        this.$emit("data-saved", registrado);
      } catch (err) {
        console.error("Erro ao salvar dados ginecológicos:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao salvar os dados");
      } finally {
        this.formLoading = false;
      }
    },

    async confirmDelete(item) {
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: "Tem certeza que deseja remover este registro?",
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) return;
      try {
        await deleteGynecology(item.id);
        this.success("Registro removido com sucesso!");
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir registro ginecológico:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover o registro");
      }
    },

    close() {
      this.internalValue = false;
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: var(--ds-white);
}

.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}

.ds-dialog-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.history-table thead th {
  background-color: var(--ds-primary-surface) !important;
  border-bottom: 2px solid #e9d5ff !important;
  letter-spacing: 0.4px;
  color: var(--ds-primary-deep) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.history-row {
  transition: background-color 0.15s ease;
}

.history-row:hover {
  background-color: var(--ds-primary-surface) !important;
}

.history-row.latest-row {
  background-color: #F4F9F6 !important;
}

.history-row td {
  border-bottom: 1px solid var(--ds-primary-border) !important;
  color: var(--ds-text-600);
}

.action-btn {
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.action-btn:hover {
  transform: scale(1.15);
}
</style>
